/**
 * Renderer, scene graph, lighting and tone mapping.
 *
 * EXPOSURE IS THE WHOLE GAME. With no art assets, the difference between "a
 * WebGL demo" and "a game" is almost entirely the light transport: ACES filmic
 * tone mapping so highlights roll off instead of clipping to white, an
 * environment term so metal and clearcoat have something to reflect, and one
 * hard key light that actually casts a shadow so the car is attached to the
 * ground. Flat ambient light plus a plain colour is what makes procedural
 * scenes look like plastic.
 *
 * The key light's shadow camera FOLLOWS the action. The arena is 8192 x 10240
 * uu; a shadow map that covers all of it spends ~5 uu per texel and the car
 * (120 uu long) gets a 24-texel blob. Following the car with a +/-2200 uu
 * frustum is 1 uu per texel instead, which is the difference between a shadow
 * with wheels in it and a grey smudge.
 */

import {
  ACESFilmicToneMapping,
  AmbientLight,
  CircleGeometry,
  Color,
  DirectionalLight,
  FogExp2,
  Group,
  HemisphereLight,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PCFSoftShadowMap,
  PMREMGenerator,
  PerspectiveCamera,
  Scene,
  SphereGeometry,
  Texture,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';
import { BALL_RADIUS } from '../physics/constants';
import { buildArena, type ArenaMesh } from './arena-mesh';
import {
  PALETTE,
  makeArenaMaterials,
  makeBallTexture,
  makeEnvironmentTexture,
  type ArenaMaterials,
} from './materials';
import { buildStadium, type StadiumEnvironment } from './environment';

// ---------------------------------------------------------------------------
// Renderer
// ---------------------------------------------------------------------------

/** Cap on devicePixelRatio. Above 2 the cost is real and the gain is not. */
const MAX_PIXEL_RATIO = 2;

export const createRenderer = (container: HTMLElement): WebGLRenderer => {
  const renderer = new WebGLRenderer({
    antialias: true,
    powerPreference: 'high-performance',
    stencil: false,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO));
  renderer.setSize(container.clientWidth, container.clientHeight);
  renderer.toneMapping = ACESFilmicToneMapping;
  // Tuned against the floodlight intensities below: bright enough that the
  // pitch markings read, dim enough that the white ball does not clip.
  //
  // Pulled DOWN from 1.15 when the stadium went in. The bowl, the crowd and the
  // roof lighting all add to the environment term, so the same exposure that
  // was right over a black void now blows the pitch out and takes the night sky
  // with it. ACES is kept over AgX: AgX desaturates its highlights, and the
  // team colours on a boosting car are the first casualty.
  renderer.toneMappingExposure = 0.98;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = PCFSoftShadowMap;
  container.appendChild(renderer.domElement);
  return renderer;
};

// ---------------------------------------------------------------------------
// Ball
// ---------------------------------------------------------------------------

export interface BallVisual {
  group: Group;
  mesh: Mesh;
  /** Fake contact shadow; the real shadow map cannot resolve the soft core. */
  contact: Mesh;
  /** Position the contact shadow under a ball at world height `y`. */
  update(y: number): void;
  dispose(): void;
}

export const buildBall = (): BallVisual => {
  const texture = makeBallTexture();
  const material = new MeshStandardMaterial({
    map: texture,
    emissiveMap: texture,
    emissive: new Color(0x6f9dff),
    // The seams glow; the panels are dark, so the emissive map does the work
    // and the overall level stays low enough not to wash out under boost.
    emissiveIntensity: 0.55,
    roughness: 0.32,
    metalness: 0.35,
    envMapIntensity: 1.3,
  });
  const mesh = new Mesh(new SphereGeometry(BALL_RADIUS, 64, 40), material);
  mesh.castShadow = true;
  mesh.receiveShadow = false;

  const contactMaterial = new MeshBasicMaterial({
    color: 0x000000,
    transparent: true,
    opacity: 0.45,
    depthWrite: false,
  });
  const contact = new Mesh(new CircleGeometry(BALL_RADIUS * 1.1, 32), contactMaterial);
  contact.rotation.x = -Math.PI / 2;
  contact.position.y = 2;
  contact.renderOrder = 2;

  const group = new Group();
  group.name = 'ball';
  group.add(mesh);

  return {
    group,
    mesh,
    contact,
    update(y: number): void {
      // Fades and widens with height, gone by two ball diameters up.
      const h = Math.max(0, y - BALL_RADIUS);
      const t = Math.min(1, h / (BALL_RADIUS * 6));
      contact.position.x = mesh.position.x + group.position.x;
      contact.position.z = mesh.position.z + group.position.z;
      contact.scale.setScalar(1 + t * 1.4);
      contactMaterial.opacity = 0.45 * (1 - t) * (1 - t);
      contact.visible = contactMaterial.opacity > 0.01;
    },
    dispose(): void {
      mesh.geometry.dispose();
      material.dispose();
      texture.dispose();
      contact.geometry.dispose();
      contactMaterial.dispose();
    },
  };
};

// ---------------------------------------------------------------------------
// Lighting
// ---------------------------------------------------------------------------

/** Half-extent of the key light's shadow frustum, uu. */
const SHADOW_EXTENT = 2200;
/** How far up the key light sits above its target. */
const KEY_LIGHT_OFFSET = new Vector3(1600, 4200, 1200);
/**
 * The rim light sits roughly opposite the key and much lower, so it grazes the
 * car's far shoulder. This is the single cheapest thing that separates a car
 * from the pitch behind it — a car lit only from the key and a hemisphere
 * dissolves into the floor the moment the two are the same value, which under a
 * night grade they always are.
 */
const RIM_LIGHT_OFFSET = new Vector3(-2100, 1150, -2600);

export interface Lighting {
  group: Group;
  key: DirectionalLight;
  rim: DirectionalLight;
  /** Re-centre the shadow frustum on a point (THREE frame). */
  focus(target: Vector3): void;
}

const buildLighting = (): Lighting => {
  const group = new Group();
  group.name = 'lighting';

  // Slightly warm, because every visible source in the stadium is a
  // floodlight — but only slightly. The pitch is a cold blue-grey and a
  // strongly tinted key turns it brown before it does anything for the cars.
  const key = new DirectionalLight(0xfff6ea, 2.7);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.camera.near = 100;
  key.shadow.camera.far = 9000;
  key.shadow.camera.left = -SHADOW_EXTENT;
  key.shadow.camera.right = SHADOW_EXTENT;
  key.shadow.camera.top = SHADOW_EXTENT;
  key.shadow.camera.bottom = -SHADOW_EXTENT;
  // At this world scale the default bias is far too small to matter; normalBias
  // in uu is what actually kills the acne on the curved fillets.
  key.shadow.bias = -0.0006;
  key.shadow.normalBias = 4;
  group.add(key, key.target);

  // Cold rim, following the action alongside the key. Casts nothing.
  const rim = new DirectionalLight(0xbcd8ff, 1.5);
  group.add(rim, rim.target);

  // One static cool fill from the far corner keeps the shadow side off black
  // without flattening the key. The second fill the old rig had is gone: the
  // stadium's environment map now does that job, and better.
  const fill = new DirectionalLight(0x8fb4ff, 0.45);
  fill.position.set(-3000, 2600, -2400);
  group.add(fill);

  // Sky/ground bounce. The sky term is the night sky's own blue and the ground
  // term is the pitch colour, so the underside of the car picks up the field
  // rather than turning grey.
  group.add(new HemisphereLight(0x6d92d8, PALETTE.floor, 0.4));
  group.add(new AmbientLight(0xa8c4ff, 0.06));

  return {
    group,
    key,
    rim,
    focus(target: Vector3): void {
      key.position.copy(target).add(KEY_LIGHT_OFFSET);
      key.target.position.copy(target);
      key.target.updateMatrixWorld();
      rim.position.copy(target).add(RIM_LIGHT_OFFSET);
      rim.target.position.copy(target);
      rim.target.updateMatrixWorld();
    },
  };
};

// ---------------------------------------------------------------------------
// The scene
// ---------------------------------------------------------------------------

export interface GameSceneOptions {
  container: HTMLElement;
  renderer?: WebGLRenderer;
}

export class GameScene {
  readonly renderer: WebGLRenderer;
  readonly scene = new Scene();
  readonly arena: ArenaMesh;
  readonly ball: BallVisual;
  readonly lighting: Lighting;
  /** The bowl, the crowd, the roof, the city and the sky. */
  readonly stadium: StadiumEnvironment;

  private readonly materials: ArenaMaterials;
  private readonly environment: Texture;
  private readonly container: HTMLElement;
  /** Wall-clock seconds since construction; drives the crowd shimmer. */
  private clock = 0;
  private lastFrameMs = 0;

  constructor(options: GameSceneOptions) {
    this.container = options.container;
    this.renderer = options.renderer ?? createRenderer(options.container);

    this.stadium = buildStadium();

    // Procedural environment: an equirect gradient with four floodlight blobs,
    // prefiltered by PMREM so rough materials get a proper blurred mip chain.
    // This is NOT the sky texture — the sky is what you see, and this is what
    // the paint reflects. They are generated from the same art direction but
    // the reflection map wants the floodlights and the bowl bounce at full
    // strength and no stars at all.
    const pmrem = new PMREMGenerator(this.renderer);
    const source = makeEnvironmentTexture();
    this.environment = pmrem.fromEquirectangular(source).texture;
    source.dispose();
    pmrem.dispose();

    this.scene.environment = this.environment;
    // The background is the real night sky, unblurred: it is seen directly
    // through the glass roof and over the top of the stands, so blurring it
    // would smear the stars into grey soup.
    //
    // INTENSITY WELL ABOVE 1. The background goes through the same ACES curve
    // as everything else, and ACES has a long toe: a night sky authored at a
    // plausible 0.18 sRGB comes out of it at 0.02, i.e. black, and the stars
    // with it. Measured on the real pipeline, the zenith needs about 2.6x
    // before it reads as a sky rather than as an unlit background clear.
    this.scene.background = this.stadium.skyTexture;
    this.scene.backgroundBlurriness = 0;
    this.scene.backgroundIntensity = 2.6;
    // Tuned so the city at ~20,000 uu reads as distant while the far goal at
    // ~10,000 uu is only lightly hazed and the pitch is essentially clear.
    this.scene.fog = new FogExp2(PALETTE.sky, 0.000045);

    this.materials = makeArenaMaterials();
    this.arena = buildArena({
      floor: this.materials.floor,
      ramp: this.materials.ramp,
      wall: this.materials.wall,
      ceiling: this.materials.ceiling,
      goalInterior: this.materials.goalInterior,
    });
    this.scene.add(this.arena.group);
    this.scene.add(this.stadium.group);

    this.ball = buildBall();
    this.scene.add(this.ball.group, this.ball.contact);

    this.lighting = buildLighting();
    this.scene.add(this.lighting.group);
  }

  /**
   * Set the crowd off. `team` is who SCORED — their end of the ground reacts
   * hardest, the other end still gets up.
   *
   * This is the one hook the environment needs from the match and it is
   * deliberately not wired here: src/main.ts owns goal events (and is being
   * revised in parallel for the goal fireworks). Calling this from the same
   * place that calls `effects.goalExplosion` is the whole integration.
   */
  celebrateGoal(team: 0 | 1): void {
    this.stadium.celebrate(team);
  }

  /** Fit the renderer and camera to the container. Safe to call every frame. */
  resize(camera: PerspectiveCamera): void {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    if (width === 0 || height === 0) return;
    const size = this.renderer.getSize(_size);
    const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
    if (size.x === width && size.y === height && this.renderer.getPixelRatio() === ratio) return;
    this.renderer.setPixelRatio(ratio);
    this.renderer.setSize(width, height, true);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
  }

  /** Drawing-buffer height in pixels — the particle shader needs it. */
  get viewportHeight(): number {
    return this.renderer.domElement.height;
  }

  render(camera: PerspectiveCamera): void {
    // The environment animates itself off the wall clock rather than off the
    // render loop's dt. That keeps the whole stadium a self-contained thing the
    // caller does not have to remember to tick — the only frame-level hook it
    // needs from outside is `celebrateGoal`.
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const dt = this.lastFrameMs === 0 ? 0 : Math.min(0.25, (now - this.lastFrameMs) / 1000);
    this.lastFrameMs = now;
    this.clock += dt;
    this.stadium.update(this.clock, dt, camera.position);

    this.renderer.render(this.scene, camera);
  }

  /** Draw calls and triangles from the last frame. For the perf report. */
  get stats(): { calls: number; triangles: number; programs: number } {
    const info = this.renderer.info;
    return {
      calls: info.render.calls,
      triangles: info.render.triangles,
      programs: info.programs?.length ?? 0,
    };
  }

  dispose(): void {
    this.materials.dispose();
    this.ball.dispose();
    this.stadium.dispose();
    this.environment.dispose();
    this.renderer.dispose();
  }
}

const _size = new Vector2();
